import type { ProductCheckoutMethod } from "schemas/generated/product";

import * as React from "react";

import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Input } from "@replo/design-system/components/input/Input";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { useProductFormEdit } from "@v2/features/products/hooks/useProductFormEdit";

interface CheckoutOption {
  id: ProductCheckoutMethod;
  title: string;
  description: string;
  iconColorSrc: string;
  iconMonoSrc: string;
}

const OPTIONS: CheckoutOption[] = [
  {
    id: "replo",
    title: "On Replo",
    description: "We process checkout and payments for you.",
    iconColorSrc: "/images/products/checkout-cart-icon-color.svg",
    iconMonoSrc: "/images/products/checkout-cart-icon.svg",
  },
  {
    id: "external",
    title: "On external page",
    description:
      "Redirect buyers to your own checkout link (e.g. Gumroad, Etsy)",
    iconColorSrc: "/images/products/checkout-cursor-icon-color.svg",
    iconMonoSrc: "/images/products/checkout-cursor-icon.svg",
  },
];

interface ProductCheckoutLinkProps {
  options?: CheckoutOption[];
}

const ExternalCheckoutLinkInput: React.FC = () => {
  const { watch, dispatchProductEdit, errors } = useProductFormEdit();
  const isMobile = useIsMobile();
  const value = watch("externalCheckoutUrl") ?? "";
  return (
    <div
      className={twMerge(
        "rounded-xl border-0.5 border-border bg-white shadow-2xs p-5 flex flex-col items-start gap-1.5",
        isMobile ? "w-full" : "w-full max-w-[600px]",
      )}
    >
      <LabeledControl
        label="External checkout link"
        size="base"
        className="w-full"
        error={errors.externalCheckoutUrl?.message}
        required
      >
        <Input
          placeholder="https://your-checkout-link.com"
          size="base"
          className="w-full"
          type="url"
          value={value}
          invalid={Boolean(errors.externalCheckoutUrl)}
          onChange={(e) => {
            return dispatchProductEdit({
              type: "changeProperty",
              payload: { key: "externalCheckoutUrl", value: e.target.value },
            });
          }}
        />
      </LabeledControl>
      <div className="text-xs text-placeholder">
        This is where visitors will be redirected to pay for your product
      </div>
    </div>
  );
};

export const ProductCheckoutLink: React.FC<ProductCheckoutLinkProps> = ({
  options,
}) => {
  const renderedOptions = options ?? OPTIONS;
  const { watch, dispatchProductEdit } = useProductFormEdit();
  const { externalCheckoutUrl, checkoutMethod } = watch();
  const isMobile = useIsMobile();
  const [selected, setSelected] = React.useState<ProductCheckoutMethod>(
    () => checkoutMethod ?? (externalCheckoutUrl ? "external" : "replo"),
  );

  return (
    <div
      className={twMerge(
        "shadow-2xs border-0.5 border-border rounded-[10px] bg-white flex flex-col items-center gap-3 p-5",
        isMobile ? "w-full" : "w-[600px]",
      )}
    >
      <div className="flex flex-col w-full gap-3">
        <div className="flex w-full items-center justify-start header-h6 text-default">
          Where customers pay
        </div>
        <div className={twMerge("flex gap-3", isMobile && "flex-col")}>
          {renderedOptions.map((option) => {
            const isSelected = option.id === selected;
            const iconSrc = isSelected
              ? option.iconColorSrc
              : option.iconMonoSrc;

            const cardClassName = twMerge(
              "relative flex items-center p-3 rounded-xl cursor-pointer select-none gap-3",
              isMobile ? "w-full h-auto" : "flex-1 h-[74px]",
              isSelected ? "bg-sheet" : "bg-white",
            );

            const overlayClassName = twMerge(
              "pointer-events-none absolute inset-0 rounded-xl z-10",
              isSelected
                ? "border-2 border-black"
                : "border-[0.5px] border-border",
            );

            return (
              <div
                key={option.id}
                className={cardClassName}
                onClick={() => {
                  setSelected(option.id);
                  dispatchProductEdit({
                    type: "changeProperty",
                    payload: { key: "checkoutMethod", value: option.id },
                  });
                }}
              >
                <div aria-hidden className={overlayClassName} />
                <img
                  src={iconSrc}
                  alt=""
                  aria-hidden
                  className="w-[35px] h-[28px]"
                />
                <div className="flex flex-col gap-0.5">
                  <div
                    className={twMerge(
                      "text-xs font-semibold",
                      isSelected ? "text-default" : "text-muted",
                    )}
                  >
                    {option.title}
                  </div>
                  <div
                    className={twMerge(
                      "text-xs leading-4",
                      isSelected ? "text-default" : "text-muted",
                    )}
                  >
                    {option.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {selected === "external" && <ExternalCheckoutLinkInput />}
    </div>
  );
};
